import { LoadingButton } from '@mui/lab';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  type SxProps,
  TextField,
  type Theme,
  Typography,
  useThemeProps,
} from '@mui/material';
import type { ComponentsPropsList } from '@mui/material/styles';
import { FORM_FIELD_THEME_NAME, V2_FIELD_HEIGHT } from '@onevision-resources/design-theme';
import { useApolloClient } from '@provision/graphql-client';
import { UrlParams } from '@provision/portal-routes';
import { Editor } from '@provision/rich-text-editor';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import 'react-querybuilder/dist/query-builder.css';
import { doNothing } from 'remeda';
import {
  DynamicIcon,
  FormAutocomplete,
  FormRichText,
  FormSelect,
  FormTextField,
  FormToggleGroup,
  Tooltip,
} from 'src/components';
import { AutoCompleteOption } from 'src/components/Forms/types';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  ActionType,
  CustomRuleGroupType,
  getCachedMacro,
  mapMacroResponseToUI,
  RecordFolderArea,
  useConversationAssigneeGroupsOptions,
  useRecordFolders,
  useSettingsMacros,
} from 'src/graphql';
import { useAiPrompts } from 'src/graphql/entities/ai-prompt';
import { MacroAudienceSelection } from 'src/graphql/entities/macro/macro-audience';
import {
  MacroFolderOption,
  MacroMessagePrivacy,
  MacrosFormActions,
  MacrosFormFields,
  MacrosFormModel,
  MacroSnippetSource,
  MacroVisibility,
} from 'src/graphql/entities/macro/macro.types';
import { useCreateRecordFolder } from 'src/graphql/entities/record-folder';
import {
  MACRO_ASSIGNMENT_ENTITIES,
  useMacroFolderAssignments,
} from 'src/graphql/entities/record-folder-assignment';
import { useUserGroupOptions } from 'src/graphql/entities/user-group';
import {
  ActionContainer,
  ActionForm,
  RulesFormContainer,
} from 'src/pages/Settings/rules/rules-form';
import {
  getDefaultActionFormState,
  muiComponents,
  PLACEHOLDER_ACTION_RULE,
} from 'src/pages/Settings/rules/rules-form-config';
import { useGlobalStore } from 'src/store';
import { useRouteParams } from 'src/utils/routing';
import { MacroAudienceField } from './macro-audience-field';
import { mapMacroToFormValues, mapRecordFoldersToOptions } from './macros-form-mappings';
import { useMacrosEngine } from './use-macros-engine';

const VISIBILITY_OPTIONS = [
  { label: 'Everyone', value: MacroVisibility.Everyone, id: MacroVisibility.Everyone },
  { label: 'Just me', value: MacroVisibility.OnlyMe, id: MacroVisibility.OnlyMe },
  {
    label: 'Shared',
    value: MacroVisibility.Shared,
    id: MacroVisibility.Shared,
  },
];

const MESSAGE_PRIVACY_OPTIONS = [
  { label: 'Public reply', value: MacroMessagePrivacy.Public, id: MacroMessagePrivacy.Public },
  {
    label: 'Internal note',
    value: MacroMessagePrivacy.Private,
    id: MacroMessagePrivacy.Private,
  },
];

const SNIPPET_SOURCE_OPTIONS = [
  {
    id: MacroSnippetSource.StaticText,
    label: 'Static text',
    value: MacroSnippetSource.StaticText,
    icon: 'scroll-text',
    iconLibrary: 'lucide' as const,
  },
  {
    // The design's four-point sparkle is the lucide one, which the portal
    // already ships for the macros toolbar icon.
    id: MacroSnippetSource.AiPrompt,
    label: 'AI prompt',
    value: MacroSnippetSource.AiPrompt,
    icon: 'sparkles',
    iconLibrary: 'lucide' as const,
  },
];

const SAVED_PROMPTS_GROUP = 'Saved prompts';

const NO_FORM_FIELD_PROPS: ComponentsPropsList['OvFormField'] = {};

const actionFieldRowSx: SxProps<Theme> = {
  height: (theme) => theme.typography.pxToRem(V2_FIELD_HEIGHT.small),
};

interface MacrosFormFrame {
  body: ReactNode;
  canSave: boolean;
  save: () => void;
}

interface MacrosFormProps {
  onSaveMacro: (values: MacrosFormModel, macroId?: string) => void;
  loading?: boolean;
  macroId?: string;
  onCancel?: () => void;
  contextPartnerId?: number | null;
  /** Lets a host such as a modal step own Save/Cancel; without it the form renders its own row. */
  renderFrame?: (frame: MacrosFormFrame) => ReactNode;
}

interface MacrosFormBodyProps extends Omit<MacrosFormFrame, 'body'> {
  renderFrame?: MacrosFormProps['renderFrame'];
  children: ReactNode;
}

const MacrosFormBody = ({ renderFrame, canSave, save, children }: MacrosFormBodyProps) => {
  const body = (
    <Stack p={1} gap={1}>
      {children}
    </Stack>
  );
  return renderFrame ? renderFrame({ body, canSave, save }) : body;
};

export const MacrosForm = ({
  onSaveMacro,
  loading,
  macroId,
  onCancel,
  contextPartnerId,
  renderFrame,
}: MacrosFormProps) => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { macroId: routeMacroId } = useRouteParams(UrlParams.macroId);
  const selectedMacroId = macroId ?? routeMacroId;
  const conversationAssigneeGroups = useConversationAssigneeGroupsOptions(partnerId);
  const { userGroupOptions, loading: userGroupOptionsLoading } = useUserGroupOptions();

  const { formValue, config, folderOptions, folderPartnerId, isFoldersLoading } =
    useMacrosForm(selectedMacroId, contextPartnerId);

  const { actionTypes, actions, timezone, queryOptions } = config;
  const { labelAbove } = useThemeProps({
    props: NO_FORM_FIELD_PROPS,
    name: FORM_FIELD_THEME_NAME,
  });
  const actionTypeOptions = useMemo(
    () => actionTypes.map(({ value, label }) => ({ id: value, value, label })),
    [actionTypes],
  );
  // The form also renders as the Settings page; only the modal adopts the modal body's look.
  const sectionTitleVariant = renderFrame ? 'subtitle2' : 'h6';
  const generalFieldsMaxWidth = renderFrame ? undefined : '30rem';
  const wideFieldsMaxWidth = renderFrame ? undefined : '50rem';

  const templateEditorRef = useRef<Editor | null>(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const { createRecordFolder, isLoading: isCreatingFolderLoading } = useCreateRecordFolder();

  const methods = useForm<MacrosFormModel>({
    defaultValues: formValue,
    mode: 'onChange',
  });
  const {
    control,
    watch,
    formState: { isValid, isDirty },
    setValue,
    handleSubmit,
    reset,
  } = methods;

  const {
    [MacrosFormFields.Actions]: actionsQuery,
    [MacrosFormFields.Visibility]: visibility,
    [MacrosFormFields.AvailableFor]: availableFor,
    [MacrosFormFields.AvailableForUserGroups]: availableForUserGroups,
    [MacrosFormFields.SnippetSource]: snippetSource,
    [MacrosFormFields.AiPromptId]: aiPromptId,
    [MacrosFormFields.AiPromptText]: aiPromptText,
  } = watch();
  const usesAiPrompt = snippetSource === MacroSnippetSource.AiPrompt;
  // A macro that carries actions can skip the snippet entirely; without actions
  // the snippet is the whole macro, so whichever branch it uses must be filled.
  const requiresSnippet = actionsQuery.length === 0;
  const requiresTemplate = requiresSnippet && !usesAiPrompt;

  const { aiPrompts, loading: isLoadingAiPrompts } = useAiPrompts({ skip: !usesAiPrompt });
  const [isWritingCustomPrompt, setIsWritingCustomPrompt] = useState(false);

  const [promptQuery, setPromptQuery] = useState('');

  // An inline prompt loaded from an existing macro puts the picker on "write a
  // custom prompt" without needing an effect to sync the two.
  const showCustomPromptField =
    isWritingCustomPrompt || Boolean(usesAiPrompt && !aiPromptId && aiPromptText);

  // The chip shows what the macro currently points at; the input beside it stays
  // usable so the choice can be changed by typing rather than cleared first.
  const chosenPromptLabel = showCustomPromptField
    ? 'Custom prompt'
    : (aiPrompts.find((prompt) => prompt.id === aiPromptId)?.friendlyName ??
      (aiPromptId ? 'Selected prompt' : ''));

  const promptOptions = useMemo<AutoCompleteOption[]>(
    () => aiPrompts.map((prompt) => ({ id: prompt.id, label: prompt.friendlyName })),
    [aiPrompts],
  );

  const handleSnippetSourceChange = useCallback(
    (nextSource: MacroSnippetSource | null) => {
      if (!nextSource || nextSource === snippetSource) {
        return;
      }

      // Only the source moves. The branch left behind keeps its value in form
      // state so toggling back is not destructive — a mis-click must not lose a
      // typed template or prompt. Exclusivity is enforced where it matters: the
      // save mapper drops the unchosen branch and the DB has a CHECK to match.
      setValue(MacrosFormFields.SnippetSource, nextSource, { shouldDirty: true });
      setPromptQuery('');
    },
    [setValue, snippetSource],
  );

  const handlePromptSelectionChange = useCallback(
    (selection: string | AutoCompleteOption | (string | AutoCompleteOption)[] | null) => {
      const option = Array.isArray(selection) ? selection[0] : selection;
      const promptId = typeof option === 'string' ? option : option?.id;

      setIsWritingCustomPrompt(false);
      setPromptQuery('');
      // Stored as the bare id: `FormAutocomplete` resolves a string field value
      // back to its option for display.
      setValue(MacrosFormFields.AiPromptId, promptId ? String(promptId) : null, {
        shouldDirty: true,
      });
      setValue(MacrosFormFields.AiPromptText, '', { shouldDirty: true });
    },
    [setValue],
  );

  const handleWriteCustomPrompt = useCallback(() => {
    setIsWritingCustomPrompt(true);
    setPromptQuery('');
    setValue(MacrosFormFields.AiPromptId, null, { shouldDirty: true });
  }, [setValue]);

  const handleClearPrompt = useCallback(() => {
    setIsWritingCustomPrompt(false);
    setPromptQuery('');
    setValue(MacrosFormFields.AiPromptId, null, { shouldDirty: true });
    setValue(MacrosFormFields.AiPromptText, '', { shouldDirty: true });
  }, [setValue]);

  const queryBuilderContext = useMemo(() => {
    return { timezone, queryOptions };
  }, [timezone, queryOptions]);

  const displayAudienceControl = visibility === MacroVisibility.Shared;

  const audienceSelection = useMemo<MacroAudienceSelection>(
    () => ({
      userGroups: availableForUserGroups ?? [],
      conversationAssigneeGroups: availableFor ?? [],
    }),
    [availableFor, availableForUserGroups],
  );
  const availableAudience = useMemo<MacroAudienceSelection>(
    () => ({
      userGroups: userGroupOptions,
      conversationAssigneeGroups,
    }),
    [conversationAssigneeGroups, userGroupOptions],
  );
  const onAudienceChange = useCallback(
    (selection: MacroAudienceSelection) => {
      setValue(MacrosFormFields.AvailableForUserGroups, selection.userGroups, {
        shouldDirty: true,
      });
      setValue(MacrosFormFields.AvailableFor, selection.conversationAssigneeGroups, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  useEffect(() => {
    if (visibility === MacroVisibility.Shared) {
      return;
    }
    if (availableFor?.length) {
      setValue(MacrosFormFields.AvailableFor, []);
    }
    if (availableForUserGroups?.length) {
      setValue(MacrosFormFields.AvailableForUserGroups, []);
    }
  }, [availableFor?.length, availableForUserGroups?.length, setValue, visibility]);

  const uncategorizedOption = useMemo<MacroFolderOption>(
    () => ({
      id: '',
      label: 'No Folder',
      value: '',
      partnerId: folderPartnerId,
    }),
    [folderPartnerId],
  );

  const folderOptionsWithEmpty = useMemo(
    () => [uncategorizedOption, ...folderOptions],
    [folderOptions, uncategorizedOption],
  );

  useEffect(() => {
    // For update operation, not all form data may be present in cache
    // on initial render, and form appears empty. As soon as data is ready, we hydrate form.
    if (formValue && !isDirty) {
      const normalizedFormValue = formValue[MacrosFormFields.RecordFolderId]
        ? formValue
        : {
            ...formValue,
            [MacrosFormFields.RecordFolderId]: uncategorizedOption,
          };

      reset(normalizedFormValue, { keepDirty: true });
    }
  }, [formValue, reset, isDirty, uncategorizedOption]);

  const handleCreateFolder = useCallback(async () => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      return;
    }

    const createdFolder = await createRecordFolder({
      name: trimmedName,
      isPrivate: false,
      icon: 'folder',
      area: RecordFolderArea.Actions,
      partnerId: folderPartnerId,
    });

    setIsCreatingFolder(false);
    setNewFolderName('');

    if (!createdFolder) {
      return;
    }

    const createdOption: MacroFolderOption = {
      id: createdFolder.id,
      label: createdFolder.name,
      value: createdFolder.id,
      partnerId: createdFolder.partnerId,
    };

    setValue(MacrosFormFields.RecordFolderId, createdOption, { shouldDirty: true });
  }, [createRecordFolder, folderPartnerId, newFolderName, setValue]);

  const { append, remove } = useFieldArray({
    name: MacrosFormFields.Actions,
    control,
  });

  const onSave = useCallback(
    (values: MacrosFormModel) => {
      return onSaveMacro(values, selectedMacroId);
    },
    [onSaveMacro, selectedMacroId],
  );

  const hasAiPrompt = Boolean(aiPromptId || aiPromptText?.trim());
  const canSubmitForm =
    isValid &&
    validateFormActions(actionsQuery) &&
    (!requiresSnippet || !usesAiPrompt || hasAiPrompt);
  const subtitle = selectedMacroId
    ? 'Update macro to add template text and actions that can be applied within the conversation, streamlining your responses and automating tasks.'
    : 'Create a macro to add template text and actions that can be applied within the conversation, streamlining your responses and automating tasks.';

  return (
    <FormProvider {...methods}>
      <MacrosFormBody
        renderFrame={renderFrame}
        canSave={canSubmitForm}
        save={() => void handleSubmit(onSave)()}
      >
        <Typography variant="body2">{subtitle}</Typography>
        <Divider sx={{ margin: '0.5rem 0' }} />
        <Typography variant={sectionTitleVariant} mb={1}>
          General
        </Typography>
        <Stack gap={2} sx={{ width: '100%', maxWidth: generalFieldsMaxWidth }}>
          <FormTextField
            required
            textFieldProps={{
              size: 'small',
              variant: 'outlined',
            }}
            name={MacrosFormFields.Name}
            label="Macro Name"
            type="textfield"
          />
          <FormTextField
            textFieldProps={{
              size: 'small',
              variant: 'outlined',
            }}
            name={MacrosFormFields.Description}
            label="Description"
            type="textfield"
          />
          <FormAutocomplete
            type="autocomplete"
            name={MacrosFormFields.RecordFolderId}
            label="Folder"
            options={folderOptionsWithEmpty}
            loading={isFoldersLoading || isCreatingFolderLoading}
            openOnFocus
            creatable
            onAddNewItem={() => setIsCreatingFolder(true)}
            onAddNewItemLabel="Create new folder"
            textFieldProps={{
              size: 'small',
              variant: 'outlined',
            }}
          />
          {isCreatingFolder ? (
            <Stack direction="row" gap={1} alignItems="center">
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="New folder name"
                value={newFolderName}
                onChange={(event) => {
                  setNewFolderName(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === 'Enter' &&
                    newFolderName.trim() &&
                    !isCreatingFolderLoading
                  ) {
                    event.preventDefault();
                    void handleCreateFolder();
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setIsCreatingFolder(false);
                    setNewFolderName('');
                  }
                }}
              />
              <LoadingButton
                loading={isCreatingFolderLoading}
                disabled={!newFolderName.trim()}
                onClick={handleCreateFolder}
                variant="contained"
              >
                Create
              </LoadingButton>
              <Button
                variant="text"
                onClick={() => {
                  setIsCreatingFolder(false);
                  setNewFolderName('');
                }}
              >
                Cancel
              </Button>
            </Stack>
          ) : null}
          <FormSelect
            required
            type="select"
            variant="outlined"
            name={MacrosFormFields.Visibility}
            label="Visibility"
            options={VISIBILITY_OPTIONS}
            size="small"
          />
          {displayAudienceControl ? (
            <MacroAudienceField
              selected={audienceSelection}
              available={availableAudience}
              loading={userGroupOptionsLoading}
              onChange={onAudienceChange}
            />
          ) : null}
        </Stack>
        <Typography variant={sectionTitleVariant} mt={2}>
          Text Snippet
        </Typography>
        <Typography variant="body2">
          Text that will be dropped into the message input field upon applying the macro.
        </Typography>
        <Stack
          direction="row"
          gap={2}
          alignItems={labelAbove ? 'flex-end' : 'center'}
          flexWrap="wrap"
        >
          <FormSelect
            required
            type="select"
            variant="outlined"
            name={MacrosFormFields.MessagePrivacy}
            label="Type"
            options={MESSAGE_PRIVACY_OPTIONS}
            sx={{ minWidth: '15rem' }}
            size="small"
          />
          <FormToggleGroup
            type="toggle-group"
            name={MacrosFormFields.SnippetSource}
            options={SNIPPET_SOURCE_OPTIONS}
            onChange={(_, nextSource) =>
              handleSnippetSourceChange(nextSource as MacroSnippetSource | null)
            }
          />
        </Stack>
        {usesAiPrompt ? (
          <Stack gap={2} sx={{ width: '100%', maxWidth: wideFieldsMaxWidth }}>
            <FormAutocomplete
              type="autocomplete"
              name={MacrosFormFields.AiPromptId}
              label="AI prompt"
              options={promptOptions}
              loading={isLoadingAiPrompts}
              openOnFocus
              // The component's own "add new" affordance is the standing
              // "Write a custom prompt" entry, same as Folder's "Create new folder".
              creatable
              onAddNewItem={handleWriteCustomPrompt}
              onAddNewItemLabel="Write a custom prompt"
              textFieldProps={{
                size: 'small',
                variant: 'outlined',
                placeholder: chosenPromptLabel
                  ? 'Change…'
                  : 'Type a prompt or select an option…',
                InputLabelProps: { shrink: true },
              }}
              // The chosen prompt reads as a chip, and the input beside it stays
              // usable so the choice can be changed by typing rather than cleared.
              inputProps={{
                startAdornment: chosenPromptLabel ? (
                  <Chip
                    size="small"
                    icon={<DynamicIcon name="sparkles" library="lucide" />}
                    label={chosenPromptLabel}
                    onDelete={handleClearPrompt}
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.custom.OV.sys.tertiaryContainer,
                      color: (theme) => theme.palette.custom.OV.sys.onTertiaryContainer,
                      fontWeight: 500,
                      '& .MuiChip-icon, & .MuiChip-deleteIcon': {
                        color: (theme) => theme.palette.custom.OV.sys.onTertiaryContainer,
                      },
                    }}
                  />
                ) : null,
              }}
              autocompleteProps={{
                forcePopupIcon: false,
                groupBy: () => SAVED_PROMPTS_GROUP,
                noOptionsText: promptQuery.trim()
                  ? `No saved prompt matches "${promptQuery.trim()}".`
                  : 'No saved prompts yet.',
                inputValue: promptQuery,
                onInputChange: (_, value, reason) => {
                  if (reason !== 'reset') {
                    setPromptQuery(value);
                  }
                },
                // `field.onChange` has already stored the whole option by the time
                // this runs; the id is what the macro persists, so it is written back.
                onChange: (_, option) => handlePromptSelectionChange(option),
                renderGroup: (params) => (
                  <li key={params.key}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        paddingX: 2,
                        paddingTop: 1,
                        textTransform: 'uppercase',
                        color: (theme) => theme.palette.text.secondary,
                      }}
                    >
                      {params.group}
                    </Typography>
                    <ul style={{ padding: 0 }}>{params.children}</ul>
                  </li>
                ),
                renderOption: (props, option) => (
                  <MenuItem {...props} key={option.id} sx={{ gap: 1 }}>
                    <DynamicIcon name="sparkles" library="lucide" />
                    {option.label}
                  </MenuItem>
                ),
              }}
            />

            {showCustomPromptField ? (
              <FormTextField
                name={MacrosFormFields.AiPromptText}
                label="Prompt"
                type="textfield"
                textFieldProps={{
                  size: 'small',
                  variant: 'outlined',
                  multiline: true,
                  minRows: 6,
                  placeholder:
                    'Describe what the reply should do — the tone, what to include, how to close…',
                }}
              />
            ) : null}
          </Stack>
        ) : (
          <FormRichText
            required={requiresTemplate ? 'Template is required' : false}
            type="rich-text"
            name={MacrosFormFields.Template}
            withSubmitButton={false}
            withToolBarOtherOptions={false}
            onSubmit={doNothing}
            placeholder="Message Contents"
            editorRef={templateEditorRef}
            fullWidth
            sx={{
              width: '100%',
              maxWidth: wideFieldsMaxWidth,
              paddingLeft: 0,
              // In the modal the text has its own bordered box, so the editor's line above its
              // toolbar would only repeat that box's bottom edge.
              ...(renderFrame && {
                '& .MuiDivider-root:not(.MuiDivider-vertical)': { display: 'none' },
              }),
              '& .portal-editor': {
                maxHeight: '15rem',
                minHeight: '8rem',
                border: '1px solid',
                padding: '1rem',
                borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
                borderRadius: (theme) => theme.shape.borderRadius / 8,
              },
            }}
          />
        )}
        <QueryBuilderMaterial muiComponents={muiComponents}>
          <Typography variant={sectionTitleVariant} mt={2}>
            Actions
          </Typography>
          <Typography variant="body2">
            Upon applying this macro and sending the message, the following actions will occur.
          </Typography>
          <RulesFormContainer gap={1} dense={Boolean(renderFrame)}>
            {actionsQuery.map((action, index) => {
              return (
                <ActionContainer key={action.id} gap={1}>
                  <Stack
                    direction="row"
                    alignItems={labelAbove ? 'flex-end' : 'center'}
                    gap={1}
                  >
                    <FormSelect
                      type="select"
                      variant="outlined"
                      size="small"
                      name={`actions.${index}.actionType`}
                      label="Action"
                      options={actionTypeOptions}
                      sx={{ minWidth: 120, alignSelf: 'start' }}
                      selectProps={{
                        onChange: (e) => {
                          const actionType = e.target.value as ActionType;
                          const formState = getDefaultActionFormState(actionType);
                          // @ts-expect-error TODO: Figure out why value is never
                          setValue(`actions.${index}.actions`, formState);
                        },
                      }}
                    />
                    {/* With the label above the field, the button centres on the field box, not the label. */}
                    <Stack
                      justifyContent="center"
                      sx={labelAbove ? actionFieldRowSx : undefined}
                    >
                      <Tooltip title="Remove this action">
                        <IconButton
                          sx={{
                            padding: 0.5,
                            borderRadius: (theme) => theme.shape.borderRadius / 8,
                          }}
                          onClick={() => remove(index)}
                        >
                          <DynamicIcon name="trash-2" library="lucide" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Controller
                    control={control}
                    name={`actions.${index}.actions`}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <ActionForm
                        key={action.actionType}
                        context={queryBuilderContext}
                        type={action.actionType}
                        value={field.value}
                        onChange={(value: unknown) => {
                          // @ts-expect-error TODO: Figure out why value is never
                          setValue(`actions.${index}.actions`, value);
                        }}
                        fields={actions[action.actionType]}
                      />
                    )}
                  />
                </ActionContainer>
              );
            })}
          </RulesFormContainer>
        </QueryBuilderMaterial>
        <Button
          sx={{ alignSelf: 'flex-start' }}
          onClick={() =>
            append({
              id: crypto.randomUUID(),
              actionType: ActionType.Data,
              actions: getDefaultActionFormState(ActionType.Data) as CustomRuleGroupType,
            })
          }
        >
          + Action
        </Button>
        {renderFrame ? null : (
          <Stack direction="row" justifyContent="flex-end" gap={1} mt={1}>
            {onCancel ? (
              <Button variant="text" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
            ) : null}
            <LoadingButton
              loading={loading}
              disabled={!canSubmitForm}
              onClick={handleSubmit(onSave)}
              variant="contained"
              sx={{ gap: 1 }}
            >
              <DynamicIcon name="save" library="lucide" />
              Save
            </LoadingButton>
          </Stack>
        )}
      </MacrosFormBody>
    </FormProvider>
  );
};

function useMacrosForm(macroId?: string, contextPartnerId?: number | null) {
  const client = useApolloClient();
  const config = useMacrosEngine();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const effectivePartnerId = contextPartnerId ?? selectedPartnerId;

  const { macros } = useSettingsMacros({
    skip: !config.actionTypes.length,
  });

  const macro = useMemo(() => {
    const loadedMacro = macros.find((b) => b.id === macroId);
    if (loadedMacro || !macroId || !client) {
      return loadedMacro;
    }

    // If macro not found in query results, try to get it from cache
    const cachedMacroRaw = getCachedMacro(client.cache, macroId);
    return cachedMacroRaw ? mapMacroResponseToUI(cachedMacroRaw) : undefined;
  }, [client, macroId, macros]);

  // Folders are scoped to the partner the macro is (or will be) saved for — the same
  // resolution the save paths use for the folder assignment's partnerId.
  const folderPartnerId = macro?.partnerId ?? effectivePartnerId ?? PLATFORM_PARTNER_ID;

  const { recordFolderAssignments } = useMacroFolderAssignments();
  const { recordFolders, isLoading: foldersLoading } = useRecordFolders({
    area: RecordFolderArea.Actions,
    assignmentEntityFilter: MACRO_ASSIGNMENT_ENTITIES,
    recordFolderAssignments,
    partnerId: effectivePartnerId,
  });

  const folderOptions = useMemo<MacroFolderOption[]>(
    () => mapRecordFoldersToOptions(recordFolders, folderPartnerId),
    [folderPartnerId, recordFolders],
  );

  return useMemo(() => {
    const formValue = {
      [MacrosFormFields.Name]: '',
      [MacrosFormFields.Description]: '',
      [MacrosFormFields.Visibility]: MacroVisibility.OnlyMe,
      [MacrosFormFields.AvailableFor]: [],
      [MacrosFormFields.AvailableForUserGroups]: [],
      [MacrosFormFields.MessagePrivacy]: MacroMessagePrivacy.Public,
      [MacrosFormFields.SnippetSource]: MacroSnippetSource.StaticText,
      [MacrosFormFields.Template]: '',
      [MacrosFormFields.AiPromptId]: null,
      [MacrosFormFields.AiPromptText]: '',
      [MacrosFormFields.Actions]: [] as MacrosFormActions,
      [MacrosFormFields.RecordFolderId]: null,
    };

    if (!macroId || !macro) {
      return {
        formValue,
        config,
        folderOptions,
        folderPartnerId,
        isFoldersLoading: foldersLoading,
      };
    }

    const selectedFolderId = recordFolderAssignments.find(
      (assignment) => assignment.entityId === macroId,
    )?.recordFolderId;

    const selectedFolderOption =
      folderOptions.find((option) => option.id === selectedFolderId) ?? null;

    return {
      formValue: {
        ...mapMacroToFormValues(macro, config),
        [MacrosFormFields.RecordFolderId]: selectedFolderOption,
      },
      config,
      folderOptions,
      folderPartnerId,
      isFoldersLoading: foldersLoading,
    };
  }, [
    config,
    macroId,
    macro,
    folderOptions,
    folderPartnerId,
    foldersLoading,
    recordFolderAssignments,
  ]);
}

function validateFormActions(actions: MacrosFormActions) {
  if (actions.length === 0) return true;
  return actions.every((action) => {
    switch (action.actionType) {
      case ActionType.Data: {
        const hasRules = action.actions.rules.length > 0;
        const hasValidRules = action.actions.rules.every((rule) => {
          if (!('field' in rule)) return true;

          return rule.field !== PLACEHOLDER_ACTION_RULE.field && !!rule.value;
        });
        return hasRules && hasValidRules;
      }
      case ActionType.ManageConversationLabels: {
        const hasLabelIds =
          Array.isArray(action.actions.labelIds) && action.actions.labelIds.length > 0;
        return !!action.actions.operation && hasLabelIds;
      }

      default:
        return true;
    }
  });
}
